import * as React from "react";
import { cn } from "../../lib/utils";

export interface FormFooterProps {
  /**
   * Helper text to display on the left side
   */
  helperText?: string;
  /**
   * Trailing text to display on the right side (e.g., character count)
   */
  trailingText?: string;
  /**
   * Validation state that affects styling and icon display
   */
  validationState?: "default" | "positive" | "negative";
  /**
   * Size variant that affects text size, icon size, and spacing
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether the footer is in a disabled state
   */
  isDisabled?: boolean;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Custom class name for the helper text
   */
  helperTextClassName?: string;
  /**
   * Custom class name for the trailing text
   */
  trailingTextClassName?: string;
}

const FormFooter = React.forwardRef<HTMLDivElement, FormFooterProps>(
  (
    {
      helperText,
      trailingText,
      validationState = "default",
      size = "medium",
      isDisabled = false,
      className,
      helperTextClassName,
      trailingTextClassName,
    },
    ref
  ) => {
    // Size-based configurations
    const sizeConfig = {
      small: {
        textClassName: "text-caption-small-regular",
        iconSize: 12,
        gap: "gap-1",
      },
      medium: {
        textClassName: "text-caption-medium-regular",
        iconSize: 14,
        gap: "gap-1",
      },
      large: {
        textClassName: "text-caption-large-regular",
        iconSize: 16,
        gap: "gap-1.5",
      },
    } as const;

    const config = sizeConfig[size];

    // Determine text color based on validation state and disabled state
    const getTextColor = () => {
      if (isDisabled) return "text-surface-ink-neutral-disabled";
      if (validationState === "positive")
        return "text-feedback-ink-positive-intense";
      if (validationState === "negative")
        return "text-feedback-ink-negative-subtle";
      if (validationState === "default")
        return "text-surface-ink-neutral-subtle";
      return "text-surface-ink-neutral-subtle";
    };
    const helperTextColorClass = getTextColor();
    const trailingTextColorClass = getTextColor();

    // Don't render anything if there's no content
    if (!helperText && !trailingText) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between px-1",
          config.gap,
          className
        )}
      >
        {helperText && (
          <div className={cn("flex items-center", config.gap)}>
            {validationState === "positive" && (
              <svg
                width={config.iconSize}
                height={config.iconSize}
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-feedback-ink-positive-intense shrink-0"
              >
                <path
                  d="M3 7L6 10L11 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {validationState === "negative" && (
              <svg
                width={config.iconSize}
                height={config.iconSize}
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-feedback-ink-negative-subtle shrink-0"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <path
                  d="M7 4V7.5M7 10V9.5"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
            )}
            <span
              className={cn(
                config.textClassName,
                helperTextColorClass,
                "italic font-size-100 leading-100",
                helperTextClassName
              )}
            >
              {helperText}
            </span>
          </div>
        )}

        {trailingText && (
          <span
            className={cn(
              config.textClassName,
              trailingTextColorClass,
              "font-size-100 leading-100 shrink-0",
              trailingTextClassName
            )}
          >
            {trailingText}
          </span>
        )}
      </div>
    );
  }
);

FormFooter.displayName = "FormFooter";

export { FormFooter };
